/* eslint-disable @typescript-eslint/naming-convention */

/**
 * Exports a {Translations} object based on the user's browser language.
 * If no translation is found for this language, the translation defaults to English.
 *
 * @exports {Translations}
 */

import { RawTranslations, Translations, UserTextCustomizations } from '@repo/types';
import nb from './translations/nb';
import nn from './translations/nn';
import en from './translations/en';
import de from './translations/de';
import es from './translations/es';
import fr from './translations/fr';
import fi from './translations/fi';
import it from './translations/it';
import nl from './translations/nl';
import cy from './translations/cy';
import ga from './translations/ga';
import { customTranslations } from './custom-translations';
import merge from 'lodash-es/merge';
import { parseTranslationTemplate } from './template-parser';

function addParsingUtilsToTranslation(translations: RawTranslations) {
    return Object.fromEntries(
        Object.entries(translations).map(([key, item]: [string, any]) => {
            if (typeof item === 'object') {
                const itemKeys = Object.keys(item);
                itemKeys.forEach((x) => {
                    if (typeof item[x] === 'function') {
                        item[x] = item[x].bind(translations);
                    }
                    if (x === 'template') {
                        item.parsed = (...args: any[]) =>
                            parseTranslationTemplate(
                                (translations[key as keyof Translations] as Record<string, any>)
                                    .template,
                                ...args,
                            );
                    }
                });
                if (itemKeys.includes('singular') && itemKeys.includes('plural')) {
                    item.num = (num: number) =>
                        num === 1
                            ? (translations[key as keyof Translations] as Record<string, any>)
                                  .singular
                            : (translations[key as keyof Translations] as Record<string, any>)
                                  .plural;
                }
            } else if ((en as any)[key]?.template) {
                return [
                    key,
                    {
                        parsed: (...args: any[]) =>
                            parseTranslationTemplate(
                                translations[key as keyof Translations] as string,
                                ...args,
                            ),
                    },
                ];
            }
            return [key, item];
        }),
    ) as Translations;
}

const nbWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, nb, customTranslations['nb'] ?? {}, customTranslations['nb-NO'] ?? {}),
);
const nnWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, nn, customTranslations['nn'] ?? {}, customTranslations['nn-NO'] ?? {}),
);
const enWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, en, customTranslations['en'] ?? {}, customTranslations['en-US'] ?? {}),
);
const deWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, de, customTranslations['de'] ?? {}, customTranslations['de-DE'] ?? {}),
);
const esWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, es, customTranslations['es'] ?? {}, customTranslations['es-ES'] ?? {}),
);
const frWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, fr, customTranslations['fr'] ?? {}, customTranslations['fr-FR'] ?? {}),
);
const fiWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, fi, customTranslations['fi'] ?? {}, customTranslations['fi-FI'] ?? {}),
);
const itWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, it, customTranslations['it'] ?? {}, customTranslations['it-IT'] ?? {}),
);
const nlWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, nl, customTranslations['nl'] ?? {}, customTranslations['nl-NL'] ?? {}),
);
const cyWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, cy, customTranslations['cy'] ?? {}, customTranslations['cy-GB'] ?? {}),
);
const gaWithCustomTranslations = addParsingUtilsToTranslation(
    merge({}, ga, customTranslations['ga'] ?? {}, customTranslations['ga-IE'] ?? {}),
);

export const allTranslations: { [locale: string]: Translations } = {
    'nb-NO': nbWithCustomTranslations,
    'nn-NO': nnWithCustomTranslations,
    'en-US': enWithCustomTranslations,
    no: nbWithCustomTranslations,
    nb: nbWithCustomTranslations,
    nn: nnWithCustomTranslations,
    en: enWithCustomTranslations,
    de: deWithCustomTranslations,
    'de-DE': deWithCustomTranslations,
    es: esWithCustomTranslations,
    'es-ES': esWithCustomTranslations,
    fr: frWithCustomTranslations,
    'fr-FR': frWithCustomTranslations,
    fi: fiWithCustomTranslations,
    'fi-FI': fiWithCustomTranslations,
    it: itWithCustomTranslations,
    'it-IT': itWithCustomTranslations,
    nl: nlWithCustomTranslations,
    'nl-NL': nlWithCustomTranslations,
    cy: cyWithCustomTranslations,
    'cy-GB': cyWithCustomTranslations,
    ga: gaWithCustomTranslations,
    'ga-IE': gaWithCustomTranslations,
};

export const defaultTranslation: Translations = enWithCustomTranslations;

const findSupportedLanguageKey = (
    locale: string,
    customTextLanguageKeys: UserTextCustomizations[keyof UserTextCustomizations],
): string | undefined => {
    if (customTextLanguageKeys === undefined) return undefined;

    const hasCustomTextForLocale = locale in customTextLanguageKeys;
    if (hasCustomTextForLocale) {
        return locale;
    }
    const [firstPart, lastPart] = locale.split('-');
    const hasCustomTextForFirstPart = firstPart in customTextLanguageKeys;
    const hasCustomTextForLastPart = lastPart in customTextLanguageKeys;
    if (hasCustomTextForFirstPart) {
        return firstPart;
    } else if (hasCustomTextForLastPart) {
        return lastPart;
    }
    return undefined;
};

export const getCustomOrDefaultText = (
    textCustomizations: UserTextCustomizations | undefined,
    textCustomizationKey: keyof UserTextCustomizations | undefined,
    locale: string,
    defaultText: string,
): string => {
    if (
        !(textCustomizations && textCustomizationKey && textCustomizationKey in textCustomizations)
    ) {
        return defaultText;
    }

    const customTextLanguageKeys = textCustomizations[textCustomizationKey];
    const translation = findSupportedLanguageKey(locale, customTextLanguageKeys);

    if (
        !translation ||
        !customTextLanguageKeys ||
        typeof customTextLanguageKeys[translation] !== 'string' // Only simple strings are supported for custom user texts
    ) {
        return defaultText;
    }

    return customTextLanguageKeys[translation] as string;
};
